import { router } from '@inertiajs/react';
import { ReactNode, useEffect, useState } from 'react';

import compileUrl from '../../helpers/url/compileUrl';
import useTrans from '../../hooks/ui/useTrans';
import AppliedCouponCode from '../../types/AppliedCouponCode';
import ErrorList from '../../types/UI/ErrorList';
import Card from '../Card/Card';
import DialogModal from '../DialogModal/DialogModal';
import { useSession } from '../SessionContext/SessionContext';

import ApplyCouponDialog from './components/ApplyCouponDialog/ApplyCouponDialog';
import CouponAppliedDialog from './components/CouponAppliedDialog/CouponAppliedDialog';
import CouponDialogIcon from './components/CouponDialogIcon/CouponDialogIcon';
import CouponInvalidDialog from './components/CouponInvalidDialog/CouponInvalidDialog';
import ValidateCouponDialog from './components/ValidateCouponDialog/ValidateCouponDialog';
import determineIconStatus from './helpers/determineIconStatus';
import determineStatus, { CouponStatus } from './helpers/determineStatus';

type CouponDialogProps = {
    visible: boolean;
    onClose: () => void;
    appliedCouponCode: AppliedCouponCode | undefined;
    mode: 'dashboard' | 'customer-area';
    currencyCode: string;
    currentUrl: string;
    applyCouponCodeUrl?: string;
    validateCouponCodeUrl?: string;
    validationParameters?: Record<string, any>;
    errors?: ErrorList;
};

const CouponDialog = ({
    visible,
    onClose,
    appliedCouponCode,
    currencyCode,
    mode,
    currentUrl,
    applyCouponCodeUrl,
    validateCouponCodeUrl,
    validationParameters,
    errors,
}: CouponDialogProps) => {
    const [localErrors, setLocalErrors] = useState<ErrorList | undefined>();
    const session = useSession();

    useEffect(() => {
        if (errors) {
            setLocalErrors(errors);
        }
    }, [errors]);

    const trans = useTrans(
        mode === 'dashboard'
            ? 'TenantDashboard.Bookings.CouponDialog.'
            : 'CustomerArea.BookingForm.CouponDialog.'
    );

    if (!visible) {
        return null;
    }

    const status = determineStatus(
        applyCouponCodeUrl,
        appliedCouponCode,
        localErrors
    );

    const suppressErrorsAndClose = () => {
        setLocalErrors(undefined);
        onClose();
    };

    const componentByStatus = (status: CouponStatus): ReactNode => {
        switch (status) {
            case 'error':
                return (
                    <CouponInvalidDialog
                        errors={localErrors ?? []}
                        onBack={suppressErrorsAndClose}
                        trans={trans}
                    />
                );

            case 'canValidate':
                return (
                    <ValidateCouponDialog
                        url={validateCouponCodeUrl!}
                        parameters={validationParameters ?? {}}
                        trans={trans}
                        onSuccess={(code) => {
                            const url = compileUrl(currentUrl, {
                                ...validationParameters,
                                couponCode: code,
                            });
                            router.get(url);
                        }}
                        onError={setLocalErrors}
                    />
                );

            case 'validated':
                return (
                    <CouponAppliedDialog
                        trans={trans}
                        coupon={appliedCouponCode!}
                        currencyCode={currencyCode}
                        onUndo={() => {
                            const url = compileUrl(currentUrl, {
                                ...validationParameters,
                                couponCode: undefined,
                            });
                            router.get(url);

                            onClose();
                        }}
                        onClose={onClose}
                    />
                );

            case 'applied':
                return (
                    <CouponAppliedDialog
                        trans={trans}
                        coupon={appliedCouponCode!}
                        currencyCode={currencyCode}
                        onUndo={() => {
                            router.post(applyCouponCodeUrl!, {
                                tenantId: session.tenantId,
                            });

                            onClose();
                        }}
                        onClose={onClose}
                    />
                );

            case 'canApply':
                return (
                    <ApplyCouponDialog
                        url={applyCouponCodeUrl!}
                        trans={trans}
                        onSuccess={onClose}
                    />
                );
        }

        return <p>Unhandled coupon status</p>;
    };

    return (
        <DialogModal
            onCloseRequested={suppressErrorsAndClose}
            width="small"
            isOpen
            unstyled
        >
            <Card variant="tint">
                <CouponDialogIcon status={determineIconStatus(status)} />
                {componentByStatus(status)}
            </Card>
        </DialogModal>
    );
};

export default CouponDialog;
